import React, { useState, useEffect } from 'react';
import { Plane, Search, RefreshCw, ArrowRight, AlertCircle, CheckCircle, XCircle, Clock, ExternalLink, Calendar as CalendarIcon, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { apiCall } from '../services/api';
import { format } from 'date-fns';

const FlightTracker = () => {
  const [flightSearchOrigin, setFlightSearchOrigin] = useState('DFW');
  const [flightSearchDestination, setFlightSearchDestination] = useState('');
  const [departureDate, setDepartureDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [filteredFlights, setFilteredFlights] = useState([]);
  const [selectedAirline, setSelectedAirline] = useState('all');
  const [flightNumberLookup, setFlightNumberLookup] = useState('');
  const [flightStatus, setFlightStatus] = useState(null);
  const [loadingFlights, setLoadingFlights] = useState(false);
  const [flightError, setFlightError] = useState(null);
  const [popularRoutes, setPopularRoutes] = useState([]);

  useEffect(() => {
    loadPopularRoutes();
  }, []);

  const loadPopularRoutes = async () => {
    try {
      const response = await apiCall('/flights/routes/popular');
      setPopularRoutes(response.routes || []);
    } catch (error) {
      console.error('Failed to load popular routes:', error);
    }
  };

  const searchFlights = async (origin, destination, depDate = null) => {
    if (!origin || !destination) {
      setFlightError('Please enter both origin and destination airports.');
      return;
    }
    
    setLoadingFlights(true);
    setFlightError(null);
    setSearchResults([]);
    setFilteredFlights([]);
    setSelectedAirline('all');
    
    try {
      const searchParams = {
        origin: origin.toUpperCase(),
        destination: destination.toUpperCase()
      };
      
      // Use provided date or default to departure date from state
      if (depDate || departureDate) {
        const dateToUse = depDate || departureDate;
        searchParams.departure_date = typeof dateToUse === 'string' ? dateToUse : format(dateToUse, 'yyyy-MM-dd');
      }
      
      const response = await apiCall('/flights/search', {
        method: 'POST',
        body: JSON.stringify(searchParams)
      });
      
      setSearchResults(response.flights || []);
      setFilteredFlights(response.flights || []);
      
      if (response.count === 0) {
        const dateStr = searchParams.departure_date ? ` on ${searchParams.departure_date}` : '';
        setFlightError(`No flights found from ${origin} to ${destination}${dateStr}.`);
      }
    } catch (error) {
      console.error('Flight search failed:', error);
      setFlightError('Failed to search flights. Please try again.');
    } finally {
      setLoadingFlights(false);
    }
  };

  const lookupFlightStatus = async () => {
    if (!flightNumberLookup.trim()) return;
    
    setLoadingFlights(true);
    setFlightError(null);
    setFlightStatus(null);
    
    try {
      const response = await apiCall(`/flights/status/${flightNumberLookup.toUpperCase()}`);
      setFlightStatus(response);
    } catch (error) {
      console.error('Flight status lookup failed:', error);
      setFlightError('Flight not found or unable to fetch status.');
    } finally {
      setLoadingFlights(false);
    }
  };

  const handleQuickRoute = (route) => {
    setFlightSearchOrigin(route.origin);
    setFlightSearchDestination(route.destination);
    searchFlights(route.origin, route.destination, departureDate);
  };

  const handleSearchClick = () => {
    searchFlights(flightSearchOrigin, flightSearchDestination, departureDate);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-400';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('scheduled') || statusLower.includes('filed')) return 'text-cyan-400';
    if (statusLower.includes('active') || statusLower.includes('en route')) return 'text-green-400';
    if (statusLower.includes('delayed')) return 'text-yellow-400';
    if (statusLower.includes('cancelled')) return 'text-red-400';
    if (statusLower.includes('arrived') || statusLower.includes('landed')) return 'text-blue-400';
    return 'text-gray-400';
  };

  const getStatusIcon = (status) => {
    if (!status) return <AlertCircle size={16} />;
    const statusLower = status.toLowerCase();
    if (statusLower.includes('scheduled') || statusLower.includes('filed')) return <Clock size={16} />;
    if (statusLower.includes('active') || statusLower.includes('en route')) return <Plane size={16} />;
    if (statusLower.includes('delayed')) return <AlertCircle size={16} />;
    if (statusLower.includes('cancelled')) return <XCircle size={16} />;
    if (statusLower.includes('arrived') || statusLower.includes('landed')) return <CheckCircle size={16} />;
    return <AlertCircle size={16} />;
  };

  const getAirlineName = (flightIdent) => {
    if (!flightIdent) return 'Unknown';
    const code = flightIdent.substring(0, 2);
    const airlines = {
      'AA': 'American Airlines',
      'UA': 'United Airlines',
      'DL': 'Delta Air Lines',
      'WN': 'Southwest Airlines',
      'AS': 'Alaska Airlines',
      'B6': 'JetBlue Airways',
      'NK': 'Spirit Airlines',
      'F9': 'Frontier Airlines',
      'G4': 'Allegiant Air',
      'SY': 'Sun Country Airlines'
    };
    return airlines[code] || flightIdent;
  };

  const getBookingUrl = (flight) => {
    const origin = flight.origin?.code_iata || flight.origin?.code || '';
    const destination = flight.destination?.code_iata || flight.destination?.code || '';
    const depDateToUse = departureDate ? (typeof departureDate === 'string' ? departureDate : format(departureDate, 'yyyy-MM-dd')) : 
                         (flight.scheduled_out ? new Date(flight.scheduled_out).toISOString().split('T')[0] : '');
    
    // Use Google Flights explore format which is more reliable
    // Format: https://www.google.com/travel/explore?tfs=params
    if (origin && destination && depDateToUse) {
      // Parse date
      const date = new Date(depDateToUse);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      
      if (returnDate) {
        // Round trip
        const retDate = new Date(typeof returnDate === 'string' ? returnDate : returnDate);
        const retYear = retDate.getFullYear();
        const retMonth = retDate.getMonth() + 1;
        const retDay = retDate.getDate();
        
        return `https://www.google.com/travel/flights?q=flights%20from%20${origin}%20to%20${destination}%20departing%20${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}%20returning%20${retYear}-${String(retMonth).padStart(2, '0')}-${String(retDay).padStart(2, '0')}`;
      } else {
        // One way - use the simple but reliable format
        return `https://www.google.com/travel/flights?q=flights%20from%20${origin}%20to%20${destination}%20on%20${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    
    // Fallback without date
    return `https://www.google.com/travel/flights?q=flights%20from%20${origin}%20to%20${destination}`;
  };

  const filterByAirline = (airlineCode) => {
    setSelectedAirline(airlineCode);
    if (airlineCode === 'all') {
      setFilteredFlights(searchResults);
    } else {
      const filtered = searchResults.filter(flight => 
        flight.ident?.startsWith(airlineCode) || flight.airline_code === airlineCode
      );
      setFilteredFlights(filtered);
    }
  };

  const getUniqueAirlines = () => {
    const airlines = {};
    searchResults.forEach(flight => {
      const code = flight.airline_code || flight.ident?.substring(0, 2);
      const name = flight.airline_name || getAirlineName(flight.ident);
      if (code && !airlines[code]) {
        airlines[code] = name;
      }
    });
    return airlines;
  };

  const getDuration = (departTime, arriveTime) => {
    if (!departTime || !arriveTime) return null;
    const depart = new Date(departTime);
    const arrive = new Date(arriveTime);
    const durationMs = arrive - depart;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="mb-8 space-y-6">
      {/* Flight Tracker Header */}
      <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Plane size={32} className="text-cyan-500" />
          <div>
            <h2 className="text-2xl font-bold text-white">Flight Tracker</h2>
            <p className="text-gray-400 text-sm">Search flights, track status, and view real-time updates</p>
          </div>
        </div>

        {/* Quick Route Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-sm text-gray-400 mr-2 self-center">Quick Routes:</span>
          {popularRoutes.map((route) => (
            <Button
              key={`${route.origin}-${route.destination}`}
              onClick={() => handleQuickRoute(route)}
              className="bg-black hover:bg-gray-900 text-white border border-white/20 text-sm"
              disabled={loadingFlights}
            >
              {route.origin} <ArrowRight size={14} className="mx-1" /> {route.destination}
            </Button>
          ))}
        </div>

        {/* Search Forms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Flight Search by Route */}
          <div className="space-y-3">
            <label className="text-sm text-gray-300 font-medium">Search Flights by Route & Date</label>
            
            {/* Origin and Destination */}
            <div className="flex gap-2">
              <Input
                placeholder="From (e.g., DFW)"
                value={flightSearchOrigin}
                onChange={(e) => setFlightSearchOrigin(e.target.value)}
                className="bg-black border-white/20 text-white"
                maxLength={3}
              />
              <ArrowRight size={20} className="text-gray-400 self-center flex-shrink-0" />
              <Input
                placeholder="To (e.g., LAX)"
                value={flightSearchDestination}
                onChange={(e) => setFlightSearchDestination(e.target.value)}
                className="bg-black border-white/20 text-white"
                maxLength={3}
              />
            </div>
            
            {/* Date Inputs with Calendar Dropdowns */}
            <div className="flex gap-2">
              {/* Departure Date Picker */}
              <div className="flex-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-black border-white/20 text-white hover:bg-gray-900 hover:text-white"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {departureDate ? format(departureDate, 'MMM dd, yyyy') : 'Select departure date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-black border-white/20 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={departureDate}
                      onSelect={setDepartureDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className="bg-black text-white border-white/20"
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-xs text-gray-500 mt-1 block">Departure</span>
              </div>

              {/* Return Date Picker */}
              <div className="flex-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-black border-white/20 text-white hover:bg-gray-900 hover:text-white"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {returnDate ? format(returnDate, 'MMM dd, yyyy') : 'Return (optional)'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-black border-white/20 z-[9999]" align="start">
                    <Calendar
                      mode="single"
                      selected={returnDate}
                      onSelect={setReturnDate}
                      disabled={(date) => {
                        const minDate = departureDate || new Date(new Date().setHours(0, 0, 0, 0));
                        return date < minDate;
                      }}
                      initialFocus
                      className="bg-black text-white border-white/20"
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-xs text-gray-500 mt-1 block">Return (optional)</span>
              </div>
            </div>
            
            {/* Search Button */}
            <Button
              onClick={handleSearchClick}
              disabled={loadingFlights || !flightSearchOrigin || !flightSearchDestination}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-semibold"
            >
              {loadingFlights ? (
                <>
                  <RefreshCw size={18} className="animate-spin mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <Search size={18} className="mr-2" />
                  Search Flights
                </>
              )}
            </Button>
          </div>

          {/* Flight Status Lookup */}
          <div className="space-y-2">
            <label className="text-sm text-gray-300 font-medium">Check Flight Status</label>
            <div className="flex gap-2">
              <Input
                placeholder="Flight # (e.g., AA123)"
                value={flightNumberLookup}
                onChange={(e) => setFlightNumberLookup(e.target.value)}
                className="bg-black border-white/20 text-white"
                onKeyPress={(e) => e.key === 'Enter' && lookupFlightStatus()}
              />
              <Button
                onClick={lookupFlightStatus}
                disabled={loadingFlights || !flightNumberLookup}
                className="bg-white hover:bg-gray-200 text-black"
              >
                {loadingFlights ? <RefreshCw size={18} className="animate-spin" /> : <Search size={18} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {flightError && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {flightError}
          </div>
        )}
      </div>

      {/* Flight Status Display */}
      {flightStatus && (
        <div className="bg-black border border-cyan-500/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-2 ${getStatusColor(flightStatus.status)}`}>
                {getStatusIcon(flightStatus.status)}
                <span className="font-bold text-lg">{flightStatus.flight_number}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(flightStatus.status)} bg-current/10`}>
                {flightStatus.status}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Departure Info */}
            <div className="space-y-3">
              <div className="text-sm text-gray-400">DEPARTURE</div>
              <div className="flex items-center gap-3">
                <div className="text-4xl font-bold text-white">{flightStatus.origin}</div>
                {flightStatus.terminal_origin && (
                  <div className="text-sm text-gray-400">
                    Terminal {flightStatus.terminal_origin}
                    {flightStatus.gate_origin && ` • Gate ${flightStatus.gate_origin}`}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Scheduled:</span>
                  <span className="text-white">{formatDate(flightStatus.scheduled_out)} {formatTime(flightStatus.scheduled_out)}</span>
                </div>
                {flightStatus.actual_out && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Actual:</span>
                    <span className="text-green-400">{formatTime(flightStatus.actual_out)}</span>
                  </div>
                )}
                {flightStatus.departure_delay > 0 && (
                  <div className="flex justify-between text-yellow-400">
                    <span>Delay:</span>
                    <span>{flightStatus.departure_delay} min</span>
                  </div>
                )}
              </div>
            </div>

            {/* Arrival Info */}
            <div className="space-y-3">
              <div className="text-sm text-gray-400">ARRIVAL</div>
              <div className="flex items-center gap-3">
                <div className="text-4xl font-bold text-white">{flightStatus.destination}</div>
                {flightStatus.terminal_destination && (
                  <div className="text-sm text-gray-400">
                    Terminal {flightStatus.terminal_destination}
                    {flightStatus.gate_destination && ` • Gate ${flightStatus.gate_destination}`}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Scheduled:</span>
                  <span className="text-white">{formatDate(flightStatus.scheduled_in)} {formatTime(flightStatus.scheduled_in)}</span>
                </div>
                {flightStatus.estimated_in && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Estimated:</span>
                    <span className="text-cyan-400">{formatTime(flightStatus.estimated_in)}</span>
                  </div>
                )}
                {flightStatus.actual_in && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Actual:</span>
                    <span className="text-green-400">{formatTime(flightStatus.actual_in)}</span>
                  </div>
                )}
                {flightStatus.arrival_delay > 0 && (
                  <div className="flex justify-between text-yellow-400">
                    <span>Delay:</span>
                    <span>{flightStatus.arrival_delay} min</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Plane size={20} className="text-cyan-500" />
              Found {filteredFlights.length} Available Flights
            </h3>
            <span className="text-sm text-gray-400">Click "Book Flight" to compare prices and book</span>
          </div>

          {/* Airline Filter Buttons */}
          <div className="bg-black/50 border border-white/10 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm text-gray-400 font-medium">Filter by Airline:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => filterByAirline('all')}
                variant={selectedAirline === 'all' ? 'default' : 'outline'}
                className={`${
                  selectedAirline === 'all' 
                    ? 'bg-cyan-500 text-black hover:bg-cyan-600' 
                    : 'bg-black border-white/20 text-white hover:bg-gray-900'
                } text-sm`}
              >
                All Airlines ({searchResults.length})
              </Button>
              {Object.entries(getUniqueAirlines()).map(([code, name]) => {
                const count = searchResults.filter(f => 
                  f.ident?.startsWith(code) || f.airline_code === code
                ).length;
                return (
                  <Button
                    key={code}
                    onClick={() => filterByAirline(code)}
                    variant={selectedAirline === code ? 'default' : 'outline'}
                    className={`${
                      selectedAirline === code 
                        ? 'bg-cyan-500 text-black hover:bg-cyan-600' 
                        : 'bg-black border-white/20 text-white hover:bg-gray-900'
                    } text-sm`}
                  >
                    {name} ({count})
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {filteredFlights.map((flight, index) => {
              const duration = getDuration(flight.scheduled_out, flight.scheduled_in);
              const airline = getAirlineName(flight.ident);
              const bookingUrl = getBookingUrl(flight);
              
              return (
                <div key={flight.fa_flight_id || index} className="bg-gradient-to-r from-black via-gray-900 to-black border border-white/20 rounded-xl p-5 hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/10">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Flight Info - Left Side */}
                    <div className="flex-1 space-y-3">
                      {/* Airline & Flight Number */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-cyan-500/10 p-2 rounded-lg">
                            <Plane size={24} className="text-cyan-500" />
                          </div>
                          <div>
                            <div className="font-bold text-white text-lg">{flight.ident}</div>
                            <div className="text-sm text-gray-400">{airline}</div>
                          </div>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${getStatusColor(flight.status)} bg-current/10`}>
                          {flight.status || 'Scheduled'}
                        </span>
                      </div>

                      {/* Route & Times */}
                      <div className="flex items-center gap-4">
                        {/* Departure */}
                        <div className="flex-1">
                          <div className="text-3xl font-bold text-white">{formatTime(flight.scheduled_out)}</div>
                          <div className="text-sm text-gray-400 mt-1">{formatDate(flight.scheduled_out)}</div>
                          <div className="text-lg font-semibold text-cyan-400 mt-1">
                            {flight.origin?.code_iata || flight.origin?.code || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {flight.origin?.city || flight.origin?.name || ''}
                          </div>
                        </div>

                        {/* Duration & Arrow */}
                        <div className="flex flex-col items-center px-4">
                          <ArrowRight size={24} className="text-cyan-500 mb-1" />
                          {duration && (
                            <div className="text-xs text-gray-400 whitespace-nowrap">{duration}</div>
                          )}
                        </div>

                        {/* Arrival */}
                        <div className="flex-1 text-right">
                          <div className="text-3xl font-bold text-white">{formatTime(flight.scheduled_in)}</div>
                          <div className="text-sm text-gray-400 mt-1">{formatDate(flight.scheduled_in)}</div>
                          <div className="text-lg font-semibold text-cyan-400 mt-1">
                            {flight.destination?.code_iata || flight.destination?.code || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {flight.destination?.city || flight.destination?.name || ''}
                          </div>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                        {flight.aircraft_type && (
                          <div className="flex items-center gap-1">
                            <Plane size={12} />
                            <span>Aircraft: {flight.aircraft_type}</span>
                          </div>
                        )}
                        {flight.gate_origin && (
                          <div>Gate: {flight.gate_origin}</div>
                        )}
                        {flight.terminal_origin && (
                          <div>Terminal: {flight.terminal_origin}</div>
                        )}
                      </div>
                    </div>

                    {/* Booking CTA - Right Side */}
                    <div className="lg:border-l lg:border-white/10 lg:pl-6 space-y-3">
                      <div className="text-center">
                        <div className="text-xs text-gray-400 mb-1">From</div>
                        <div className="text-2xl font-bold text-green-400 mb-1">
                          ${flight.price || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">per person</div>
                      </div>
                      <Button
                        onClick={() => window.open(bookingUrl, '_blank', 'noopener,noreferrer')}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold"
                      >
                        <CalendarIcon size={16} className="mr-2" />
                        Book Flight
                        <ExternalLink size={14} className="ml-2" />
                      </Button>
                      <div className="text-xs text-center text-gray-500">
                        Compare prices on Google Flights
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlightTracker;
